import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import localForage from 'localforage';
import { useBaseUrl } from '../../../utils/utils';
import DashboardHeader from "../../layouts/dashboard_header";
import SideMenu from "../../layouts/dashboard_menu";
import DashboardFooter from "../../layouts/dashboard_footer";

interface Patient {
  patient_id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  phone: string;
  email: string;
  gender: string;
}

interface Clinic {
  id: number;
  name: string;
  email: string;
  phone: string;
  facility_code?: string;
}

interface ReferralType {
  value: string;
  label: string;
}

interface Service {
  id: number;
  name: string;
  category: string;
}

interface MedicalRecord {
  id: string;
  title: string;
  type: string;
  date: string;
  summary: string;
}

interface FormData {
  step: number;
  patient: Patient | null;
  patientId: string;
  facilityCode: string;
  receivingClinic: Clinic | null;
  referralType: string;
  priority: string;
  selectedServices: Service[];
  selectedRecords: MedicalRecord[];
  reasonForReferral: string;
  clinicalSummary: string;
  requestedServices: string;
}

const CreateReferralRequest: React.FC = () => {
  const navigate = useNavigate();
  const baseUrl = useBaseUrl();
  const [clinicId, setClinicId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState<FormData>({
    step: 1,
    patient: null,
    patientId: '',
    facilityCode: '',
    receivingClinic: null,
    referralType: '',
    priority: '',
    selectedServices: [],
    selectedRecords: [],
    reasonForReferral: '',
    clinicalSummary: '',
    requestedServices: ''
  });

  // Available options (will be fetched from backend)
  const [referralTypes, setReferralTypes] = useState<ReferralType[]>([]);
  const [priorities, setPriorities] = useState<ReferralType[]>([]);
  const [availableServices, setAvailableServices] = useState<Service[]>([]);
  const [patientRecords, setPatientRecords] = useState<MedicalRecord[]>([]);

  useEffect(() => {
    loadClinicId();
    fetchReferralOptions();
  }, []);

  const loadClinicId = async () => {
    try {
      const id = await localForage.getItem<number>('clinic_id');
      if (id) {
        setClinicId(id);
      }
    } catch (error) {
      console.error('Failed to load clinic ID:', error);
    }
  };

  const fetchReferralOptions = async () => {
    try {
      // Fetch referral types and priorities from backend
      setReferralTypes([
        { value: 'consultation', label: 'Consultation' },
        { value: 'emergency', label: 'Emergency Referral' },
        { value: 'specialist', label: 'Specialist Care' },
        { value: 'diagnostic', label: 'Diagnostic Services' },
        { value: 'treatment', label: 'Treatment' }
      ]);

      setPriorities([
        { value: 'routine', label: 'Routine' },
        { value: 'urgent', label: 'Urgent' },
        { value: 'emergency', label: 'Emergency' }
      ]);
    } catch (error) {
      console.error('Failed to fetch referral options:', error);
    }
  };

  const validatePatient = async () => {
    if (!formData.patientId.trim()) {
      setError('Please enter a patient ID');
      return;
    }

    if (!clinicId) {
      setError('Clinic ID not found. Please login again.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${baseUrl}facilities/patients/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          patient_id: formData.patientId,
          clinic_id: clinicId
        }),
      });

      const data = await response.json();

      if (data.success && data.patient) {
        setFormData(prev => ({
          ...prev,
          patient: data.patient
        }));
      } else {
        setError(data.error || 'Patient not found or not internal to your facility');
      }
    } catch (error) {
      setError('Failed to validate patient. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const validateFacility = async () => {
    if (!formData.facilityCode.trim()) {
      setError('Please enter a facility code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${baseUrl}facilities/validate-facility`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          facility_code: formData.facilityCode
        }),
      });

      const data = await response.json();

      if (data.success && data.clinic) {
        setFormData(prev => ({
          ...prev,
          receivingClinic: data.clinic
        }));
      } else {
        setError(data.error || 'Facility not found or invalid facility code');
      }
    } catch (error) {
      setError('Failed to validate facility. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    if (!formData.receivingClinic) return;

    setLoading(true);
    try {
      const response = await fetch(`${baseUrl}facilities/services?clinic_id=${formData.receivingClinic.id}`);
      const data = await response.json();

      if (data.success) {
        setAvailableServices(data.services || []);
      }
    } catch (error) {
      console.error('Failed to fetch services:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPatientRecords = async () => {
    if (!formData.patient) return;

    setLoading(true);
    try {
      const response = await fetch(`${baseUrl}patients/medical-records?patient_id=${formData.patient.patient_id}&clinic_id=${clinicId}`);
      const data = await response.json();

      if (data.success) {
        setPatientRecords(data.records || []);
      }
    } catch (error) {
      console.error('Failed to fetch patient records:', error);
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (formData.step === 2 && formData.receivingClinic) {
      fetchServices();
    }
    if (formData.step === 4 && formData.patient) {
      fetchPatientRecords();
    }
    setFormData(prev => ({ ...prev, step: prev.step + 1 }));
    setError('');
  };

  const prevStep = () => {
    setFormData(prev => ({ ...prev, step: prev.step - 1 }));
    setError('');
  };

  const handleServiceToggle = (service: Service) => {
    setFormData(prev => ({
      ...prev,
      selectedServices: prev.selectedServices.find(s => s.id === service.id)
        ? prev.selectedServices.filter(s => s.id !== service.id)
        : [...prev.selectedServices, service]
    }));
  };

  const handleRecordToggle = (record: MedicalRecord) => {
    setFormData(prev => ({
      ...prev,
      selectedRecords: prev.selectedRecords.find(r => r.id === record.id)
        ? prev.selectedRecords.filter(r => r.id !== record.id)
        : [...prev.selectedRecords, record]
    }));
  };

  const submitReferral = async () => {
    if (!clinicId || !formData.patient || !formData.receivingClinic) {
      setError('Missing required information');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create referral request
      const referralData = {
        patient_id: formData.patient.patient_id,
        receiving_clinic_id: formData.receivingClinic.id,
        referral_type: formData.referralType,
        priority: formData.priority,
        reason_for_referral: formData.reasonForReferral,
        clinical_summary: formData.clinicalSummary,
        requested_services: formData.requestedServices,
        selected_services: formData.selectedServices.map(s => s.id),
        clinic_id: clinicId
      };

      const response = await fetch(`${baseUrl}referrals/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(referralData),
      });

      const data = await response.json();

      if (data.success) {
        // Request patient consent with selected records
        const consentData = {
          referral_id: data.referral.referral_id,
          records_to_share: formData.selectedRecords.map(record => ({
            title: record.title,
            type: record.type,
            date: record.date,
            summary: record.summary,
            record_id: record.id,
            is_critical: false
          })),
          purpose_of_sharing: formData.reasonForReferral
        };

        await fetch(`${baseUrl}referrals/request-consent`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(consentData),
        });

        alert(`Referral request created successfully! Referral ID: ${data.referral.referral_id}\n\nPatient consent request has been sent.`);
        navigate('/institution/dashboard/patients');
      } else {
        setError(data.error || 'Failed to create referral request');
      }
    } catch (error) {
      setError('Failed to submit referral request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (formData.step) {
      case 1:
        return (
          <div>
            <h3 className="mb-4">Step 1: Select Patient</h3>
            <div className="alert alert-info">
              <i className="ri-information-line me-2"></i>
              Enter the Patient ID of an internal patient you want to refer. 
              You can only refer patients that belong to your facility.
            </div>
            
            <div className="row mb-4">
              <div className="col-md-8">
                <label className="form-label">
                  Patient ID <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.patientId}
                  onChange={(e) => setFormData(prev => ({ ...prev, patientId: e.target.value, patient: null }))}
                  placeholder="Enter patient ID"
                />
              </div>
              <div className="col-md-4 d-flex align-items-end">
                <button
                  type="button"
                  onClick={validatePatient}
                  disabled={loading || !formData.patientId.trim()}
                  className="btn btn-primary w-100"
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Validating...
                    </>
                  ) : (
                    'Validate Patient'
                  )}
                </button>
              </div>
            </div>

            {formData.patient && (
              <div className="alert alert-success">
                <h6 className="alert-heading">
                  <i className="ri-check-line me-2"></i>Patient Validated
                </h6>
                <div className="row">
                  <div className="col-md-6">
                    <strong>Name:</strong> {formData.patient.first_name} {formData.patient.last_name}
                  </div>
                  <div className="col-md-6">
                    <strong>DOB:</strong> {formData.patient.date_of_birth}
                  </div>
                  <div className="col-md-6">
                    <strong>Phone:</strong> {formData.patient.phone}
                  </div>
                  <div className="col-md-6">
                    <strong>Gender:</strong> {formData.patient.gender}
                  </div>
                </div>
              </div>
            )}

            <div className="d-flex justify-content-end">
              <button
                type="button"
                onClick={nextStep}
                disabled={!formData.patient}
                className="btn btn-primary"
              >
                Next <i className="ri-arrow-right-line ms-1"></i>
              </button>
            </div>
          </div>
        );

      case 2:
        return (
          <div>
            <h3 className="mb-4">Step 2: Receiving Facility</h3>
            <div className="alert alert-info">
              <i className="ri-information-line me-2"></i>
              Enter the facility code of the clinic you want to refer the patient to.
            </div>
            
            <div className="row mb-4">
              <div className="col-md-8">
                <label className="form-label">
                  Facility Code <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.facilityCode}
                  onChange={(e) => setFormData(prev => ({ ...prev, facilityCode: e.target.value, receivingClinic: null }))}
                  placeholder="Enter facility code"
                />
              </div>
              <div className="col-md-4 d-flex align-items-end">
                <button
                  type="button"
                  onClick={validateFacility}
                  disabled={loading || !formData.facilityCode.trim()}
                  className="btn btn-primary w-100"
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Validating...
                    </>
                  ) : (
                    'Validate Facility'
                  )}
                </button>
              </div>
            </div>

            {formData.receivingClinic && (
              <div className="alert alert-success">
                <h6 className="alert-heading">
                  <i className="ri-check-line me-2"></i>Facility Validated
                </h6>
                <div className="row">
                  <div className="col-md-6">
                    <strong>Name:</strong> {formData.receivingClinic.name}
                  </div>
                  <div className="col-md-6">
                    <strong>Email:</strong> {formData.receivingClinic.email}
                  </div>
                  <div className="col-md-6">
                    <strong>Phone:</strong> {formData.receivingClinic.phone}
                  </div>
                </div>
              </div>
            )}

            <div className="d-flex justify-content-between">
              <button
                type="button"
                onClick={prevStep}
                className="btn btn-secondary"
              >
                <i className="ri-arrow-left-line me-1"></i>Back
              </button>
              <button
                type="button"
                onClick={nextStep}
                disabled={!formData.receivingClinic}
                className="btn btn-primary"
              >
                Next <i className="ri-arrow-right-line ms-1"></i>
              </button>
            </div>
          </div>
        );

      case 3:
        return (
          <div>
            <h3 className="mb-4">Step 3: Referral Type & Priority</h3>
            
            <div className="row mb-4">
              <div className="col-md-6">
                <label className="form-label">
                  Referral Type <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  value={formData.referralType}
                  onChange={(e) => setFormData(prev => ({ ...prev, referralType: e.target.value }))}
                >
                  <option value="">Select referral type</option>
                  {referralTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-6">
                <label className="form-label">
                  Priority <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  value={formData.priority}
                  onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                >
                  <option value="">Select priority</option>
                  {priorities.map(priority => (
                    <option key={priority.value} value={priority.value}>{priority.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="d-flex justify-content-between">
              <button
                type="button"
                onClick={prevStep}
                className="btn btn-secondary"
              >
                <i className="ri-arrow-left-line me-1"></i>Back
              </button>
              <button
                type="button"
                onClick={nextStep}
                disabled={!formData.referralType || !formData.priority}
                className="btn btn-primary"
              >
                Next <i className="ri-arrow-right-line ms-1"></i>
              </button>
            </div>
          </div>
        );

      case 4:
        return (
          <div>
            <h3 className="mb-4">Step 4: Select Services Required</h3>
            <div className="alert alert-info">
              <i className="ri-information-line me-2"></i>
              Select the medical services you're requesting from the receiving facility.
            </div>

            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted">Loading services...</p>
              </div>
            ) : (
              <div className="border rounded p-3 mb-4" style={{maxHeight: '300px', overflowY: 'auto'}}>
                {availableServices.length > 0 ? (
                  <div>
                    {availableServices.map(service => (
                      <div key={service.id} className="form-check mb-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`service-${service.id}`}
                          checked={formData.selectedServices.some(s => s.id === service.id)}
                          onChange={() => handleServiceToggle(service)}
                        />
                        <label className="form-check-label" htmlFor={`service-${service.id}`}>
                          <div className="fw-medium">{service.name}</div>
                          {service.category && (
                            <small className="text-muted">{service.category}</small>
                          )}
                        </label>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted">
                    <i className="ri-service-line display-4"></i>
                    <p className="mt-2">No services available for this facility</p>
                  </div>
                )}
              </div>
            )}

            {formData.selectedServices.length > 0 && (
              <div className="alert alert-success">
                <h6 className="alert-heading">Selected Services ({formData.selectedServices.length})</h6>
                <div className="d-flex flex-wrap gap-1">
                  {formData.selectedServices.map(service => (
                    <span key={service.id} className="badge bg-success">
                      {service.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="d-flex justify-content-between">
              <button
                type="button"
                onClick={prevStep}
                className="btn btn-secondary"
              >
                <i className="ri-arrow-left-line me-1"></i>Back
              </button>
              <button
                type="button"
                onClick={nextStep}
                className="btn btn-primary"
              >
                Next <i className="ri-arrow-right-line ms-1"></i>
              </button>
            </div>
          </div>
        );

      case 5:
        return (
          <div>
            <h3 className="mb-4">Step 5: Select Medical Records to Share</h3>
            <div className="alert alert-warning">
              <i className="ri-shield-check-line me-2"></i>
              Select which medical records you want to include with this referral. 
              The patient will be asked to consent to sharing these records.
            </div>

            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted">Loading medical records...</p>
              </div>
            ) : (
              <div className="border rounded p-3 mb-4" style={{maxHeight: '300px', overflowY: 'auto'}}>
                {patientRecords.length > 0 ? (
                  <div>
                    {patientRecords.map(record => (
                      <div key={record.id} className="form-check mb-3 p-2 border-bottom">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`record-${record.id}`}
                          checked={formData.selectedRecords.some(r => r.id === record.id)}
                          onChange={() => handleRecordToggle(record)}
                        />
                        <label className="form-check-label w-100" htmlFor={`record-${record.id}`}>
                          <div className="fw-medium">{record.title}</div>
                          <small className="text-muted">{record.type} • {record.date}</small>
                          {record.summary && (
                            <div className="small text-secondary mt-1">{record.summary}</div>
                          )}
                        </label>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted">
                    <i className="ri-file-list-3-line display-4"></i>
                    <p className="mt-2">No medical records found for this patient</p>
                  </div>
                )}
              </div>
            )}

            {formData.selectedRecords.length > 0 && (
              <div className="alert alert-success">
                <h6 className="alert-heading">Selected Records ({formData.selectedRecords.length})</h6>
                <ul className="mb-0">
                  {formData.selectedRecords.map(record => (
                    <li key={record.id} className="small">
                      {record.title} ({record.type})
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="d-flex justify-content-between">
              <button
                type="button"
                onClick={prevStep}
                className="btn btn-secondary"
              >
                <i className="ri-arrow-left-line me-1"></i>Back
              </button>
              <button
                type="button"
                onClick={nextStep}
                className="btn btn-primary"
              >
                Next <i className="ri-arrow-right-line ms-1"></i>
              </button>
            </div>
          </div>
        );

      case 6:
        return (
          <div>
            <h3 className="mb-4">Step 6: Additional Information</h3>
            
            <div className="mb-4">
              <label className="form-label">
                Reason for Referral <span className="text-danger">*</span>
              </label>
              <textarea
                className="form-control"
                value={formData.reasonForReferral}
                onChange={(e) => setFormData(prev => ({ ...prev, reasonForReferral: e.target.value }))}
                rows={3}
                placeholder="Describe why you are referring this patient..."
              />
            </div>

            <div className="mb-4">
              <label className="form-label">
                Clinical Summary <span className="text-danger">*</span>
              </label>
              <textarea
                className="form-control"
                value={formData.clinicalSummary}
                onChange={(e) => setFormData(prev => ({ ...prev, clinicalSummary: e.target.value }))}
                rows={4}
                placeholder="Provide a clinical summary of the patient's condition..."
              />
            </div>

            <div className="mb-4">
              <label className="form-label">
                Requested Services Description
              </label>
              <textarea
                className="form-control"
                value={formData.requestedServices}
                onChange={(e) => setFormData(prev => ({ ...prev, requestedServices: e.target.value }))}
                rows={3}
                placeholder="Describe the specific services or care you're requesting..."
              />
            </div>

            <div className="d-flex justify-content-between">
              <button
                type="button"
                onClick={prevStep}
                className="btn btn-secondary"
              >
                <i className="ri-arrow-left-line me-1"></i>Back
              </button>
              <button
                type="button"
                onClick={nextStep}
                disabled={!formData.reasonForReferral.trim() || !formData.clinicalSummary.trim()}
                className="btn btn-primary"
              >
                Next <i className="ri-arrow-right-line ms-1"></i>
              </button>
            </div>
          </div>
        );

      case 7:
        return (
          <div>
            <h3 className="mb-4">Step 7: Review & Submit</h3>
            
            <div className="card bg-light mb-4">
              <div className="card-header">
                <h5 className="card-title mb-0">Referral Summary</h5>
              </div>
              <div className="card-body">
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Patient:</strong> {formData.patient?.first_name} {formData.patient?.last_name}
                  </div>
                  <div className="col-md-6">
                    <strong>Patient ID:</strong> {formData.patient?.patient_id}
                  </div>
                  <div className="col-md-6">
                    <strong>Receiving Facility:</strong> {formData.receivingClinic?.name}
                  </div>
                  <div className="col-md-6">
                    <strong>Referral Type:</strong> {referralTypes.find(t => t.value === formData.referralType)?.label}
                  </div>
                  <div className="col-md-6">
                    <strong>Priority:</strong> {priorities.find(p => p.value === formData.priority)?.label}
                  </div>
                  <div className="col-md-6">
                    <strong>Services:</strong> {formData.selectedServices.length} selected
                  </div>
                  <div className="col-md-6">
                    <strong>Records to Share:</strong> {formData.selectedRecords.length} selected
                  </div>
                </div>

                <div className="mb-3">
                  <strong>Reason for Referral:</strong>
                  <p className="text-muted mt-1">{formData.reasonForReferral}</p>
                </div>

                <div className="mb-3">
                  <strong>Clinical Summary:</strong>
                  <p className="text-muted mt-1">{formData.clinicalSummary}</p>
                </div>

                {formData.requestedServices && (
                  <div className="mb-3">
                    <strong>Requested Services:</strong>
                    <p className="text-muted mt-1">{formData.requestedServices}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="alert alert-warning">
              <h6 className="alert-heading">
                <i className="ri-shield-check-line me-2"></i>Patient Consent Required
              </h6>
              <p className="mb-0">
                After submitting this referral, the patient will receive a consent request to approve 
                sharing the selected medical records with the receiving facility.
              </p>
            </div>

            <div className="d-flex justify-content-between">
              <button
                type="button"
                onClick={prevStep}
                className="btn btn-secondary"
              >
                <i className="ri-arrow-left-line me-1"></i>Back
              </button>
              <button
                type="button"
                onClick={submitReferral}
                disabled={loading}
                className="btn btn-success px-4"
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Submitting...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line me-2"></i>
                    Submit Referral Request
                  </>
                )}
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <DashboardHeader />
      <SideMenu />
      
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            {/* Page Title */}
            <div className="row">
              <div className="col-12">
                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 className="mb-sm-0">Create Referral Request</h4>
                  <div className="page-title-right">
                    <ol className="breadcrumb m-0">
                      <li className="breadcrumb-item"><a href="/institution/dashboard">Dashboard</a></li>
                      <li className="breadcrumb-item"><a href="/institution/patients">Patient Center</a></li>
                      <li className="breadcrumb-item active">Create Referral</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            {/* Progress Header */}
            <div className="row">
              <div className="col-12">
                <div className="card">
                  <div className="card-header">
                    <h4 className="card-title mb-0">New Patient Referral Request</h4>
                  </div>
                  <div className="card-body">
                    <p className="text-muted">
                      Follow the steps below to create a new patient referral request with medical record sharing consent.
                    </p>
                    
                    {/* Progress Bar */}
                    <div className="mt-4">
                      <div className="d-flex justify-content-between text-muted small mb-2">
                        <span>Step {formData.step} of 7</span>
                        <span>{Math.round((formData.step / 7) * 100)}% Complete</span>
                      </div>
                      <div className="progress progress-sm">
                        <div 
                          className="progress-bar bg-primary"
                          role="progressbar"
                          style={{ width: `${(formData.step / 7) * 100}%` }}
                          aria-valuenow={formData.step}
                          aria-valuemin={0}
                          aria-valuemax={7}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Content */}
            <div className="row">
              <div className="col-12">
                <div className="card">
                  <div className="card-body">
                    {error && (
                      <div className="alert alert-danger d-flex align-items-center" role="alert">
                        <i className="ri-error-warning-line me-2"></i>
                        <div>{error}</div>
                      </div>
                    )}

                    {renderStep()}
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="row">
              <div className="col-12">
                <div className="text-center">
                  <button
                    onClick={() => navigate('/institution/patients')}
                    className="btn btn-link text-decoration-none"
                  >
                    <i className="ri-arrow-left-line me-1"></i>
                    Back to Patient Center
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <DashboardFooter />
    </>
  );
};

export default CreateReferralRequest;
